import { ComponentConsumerProps, ComponentRendering, Placeholder, RichText, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useRef, FC } from 'react';
import breakpoints from '../../constants/breakpoints.constants';
import ctaTypes from '../../enums/CTA.enums';
import useMediaQuery from '../../hooks/useMediaQuery';
import { targetLinkdsk } from '../../utils/targetDskLink';
import CTA from '../@shared/CTA';
import { CustomText } from '../@shared/customText';
import DownloadCTA from '../@shared/downloadCTA';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { HeroBannerModel } from './models/heroBanner.model';
import { HeroBannerProps } from './types/heroBanner.types';
import MediaImage from './mediaImage';
import FadeDown from '../@shared/fadeDownEffect';
import '../../styles/@shared/hero-banner.scss';
import './styles.scss';

const SmallHero: FC<ComponentConsumerProps & HeroBannerProps> = (props) => {
  const isEE = props?.sitecoreContext?.pageEditing;
  const model = new HeroBannerModel(props);
  const desktopImage = model.image?.value?.src ? 'image' : 'noImage';
  const mobileImage = model.mobileImage?.value?.src ? 'image' : 'noImage';
  const smallHeroRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const placeholderData = props.sitecoreContext?.route?.placeholders['jss-main'] || [];

  useEffect(() => {
    if (isEE) return;

    if (smallHeroRef) {
      const smallHeroContentEl = smallHeroRef?.current?.querySelector('.hero-banner__wrapper') as HTMLDivElement;
      if (!smallHeroContentEl) return;
      smallHeroContentEl.style.minHeight = `${
        isDesktop ? model.image?.value?.height : (model.mobileImage?.value?.height || model.image?.value?.height) ?? 0
      }px`;
    }
  }, [smallHeroRef, isEE, isDesktop]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  let delay = 1; // in seconds
  const getNextDelay = () => delay++ * 1000;

  return (
    <>
      {!!model.dataSource && (
        <div className={`hero-banner small-hero${model.isGradientDisabled ? ' small-hero--no-background' : ''}`} data-text-color={model?.textColor?.value?.toLowerCase()} ref={smallHeroRef}>
          <div className="hero-banner__wrapper" dsk-image={desktopImage} mbl-image={mobileImage}>
            <div className="hero-banner__content" data-text-color={model?.textColor?.value?.toLowerCase()}>
              {model?.eyebrow && (
                <FadeDown duration={1000} delay={getNextDelay()}>
                  <Text tag="p" field={model?.eyebrow} className="hero-banner__eyebrow" />
                </FadeDown>
              )}
              <FadeDown duration={1000} delay={getNextDelay()}>
                <CustomText tag={model.headingTag} field={model?.title} className="hero-banner__title" />
              </FadeDown>
              {model?.description && (
                <FadeDown duration={1000} delay={getNextDelay()}>
                  <RichText tag="div" field={model?.description} className="rich-text color-inherit-link hero-banner__description" />
                </FadeDown>
              )}
              {model?.isPrimaryLinkExist ||
              model?.validSecondaryLinkExists ||
              (!model.validSecondaryLinkExists && model.downloadButtonLink) ? (
                <FadeDown duration={1000} delay={getNextDelay()}>
                  <div className="small-hero__button-wrapper">
                    {(isEE || (model?.primaryLink?.value?.href && model?.primaryLink?.value?.text)) && (
                      <span>
                        <CTA
                          type={ctaTypes.primaryOrange}
                          field={model?.primaryLink}
                          target={targetLinkdsk(model?.primaryLink, isDesktop)}
                        />
                      </span>
                    )}
                    {model.validSecondaryLinkExists && (
                      <span>
                        <CTA
                          type={ctaTypes.primaryWhite}
                          field={model?.secondaryLink}
                          target={targetLinkdsk(model?.secondaryLink, isDesktop)}
                        />
                      </span>
                    )}
                    {!model.validSecondaryLinkExists && model.downloadButtonLink && (
                      <DownloadCTA field={model.downloadButtonLink} type={ctaTypes['primaryWhite']} />
                    )}
                    <span className={isEE ? 'generic-popupEE-width' : ''}>
                      <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
                    </span>
                  </div>
                </FadeDown>
              ) : null}
            </div>
            <MediaImage
              mobileHeroImage={model?.mobileImage}
              desktopHeroImage={model?.image}
              dataSource={model.dataSource}
              placeholderData={placeholderData}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default withSitecoreContext()(SmallHero);
